import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { collection, collectionGroup, doc, Firestore, getDocs, query, runTransaction, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Attendee } from 'src/@types/attendee';
import { AUTH, DB, FUNCTIONS } from 'src/contexts/FirebaseContext';

interface RefundRequest {
  amount: number;
  ticketid: string;
  eventid: string;
  reservationid: string;
  pi: string;
  userid: string;
}

interface RefundResponse {
  id: string;
  object: 'refund';
  status: string;
  // Add other Stripe refund response fields as needed
}

interface BusRefundRequest {
  amount: number;
  busTicketId: string;
  eventId: string;
  reservationId: string;
  pi: string;
  userId: string;
  ticketType: 'bus';
}

interface BusRefundResponse {
  success: boolean;
  refundId: string;
  amount: number;
  busTicketId: string;
  message: string;
}

interface UpdateReservationUserInfo {
  reservationId: string;
  firstName: string;
  lastName: string;
  email: string;
  ticketId: string;  // Add this to know which ticket to update
  userId: string; 
  isReservationEdit: boolean;
}

interface UpdateTicketUserInfo {
  reservationId: string;
  ticketId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}



export const ReservationsApi = createApi({
    reducerPath: 'ReservationsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Attendees', 'Tickets', 'Reservations', 'Reservation', 'BusTicket', 'Event'],
  endpoints: (builder) => ({
    getAttendees: builder.query<Attendee[], { eventId: string; status: string; outputFormat: string }>({
      async queryFn(args) {
        try {
          const fetchReservationsCallable = httpsCallable<
            { eventId: string; status: string; outputFormat: string },
            { attendees: Attendee[] }
          >(FUNCTIONS, 'reservations-fetchReservationsCallable');
          
          const result = await fetchReservationsCallable(args);
          return { data: result.data.attendees };
        } catch (error: any) {
          console.error(error);
          return { error: error.message };
        }
      },
      providesTags: ['Attendees'],
    }),
    getConfirmedTicketIds: builder.query<string[], string>({
      async queryFn(eventId) {
        try {
          const ticketsRef = collectionGroup(DB, 'tickets');
          const q = query(
            ticketsRef, 
            where('eventid', '==', eventId),
            where('status', '==', 'confirmed')
          );
          const querySnapshot = await getDocs(q);
          const ticketIds: string[] = [];
          querySnapshot.forEach((doc) => {
            ticketIds.push(doc.id);
          });
          return { data: ticketIds };
        } catch (error: any) {
          return { error: error.message };
        }
      },
    }),
    updateReservationUserInfo: builder.mutation({
      queryFn: async ({ 
        reservationId, 
        ticketId, 
        userId, 
        firstName, 
        lastName, 
        email,
        isReservationEdit = false
      }) => {
        try {
          const batch = writeBatch(DB);
          
          if (isReservationEdit) {
            // In reservation edit mode, ONLY update the reservation
            const reservationDocRef = doc(DB, "reservations", reservationId);
            
            const updateData: Record<string, any> = {
              "client.name": `${firstName} ${lastName}`,
              "client.firstname": firstName,
              "client.lastname": lastName,
              "userinfos.name": `${firstName} ${lastName}`,
              "userinfos.firstName": firstName,
              "userinfos.lastName": lastName,
              "lastUpdatedAt": serverTimestamp(),
              "lastUpdatedBy": userId
            };
            
            // Only update email if provided
            if (email) {
              updateData["client.email"] = email;
              updateData["userinfos.email"] = email;
            }
            
            batch.update(reservationDocRef, updateData);
          } else {
            // In ticket edit mode, ONLY update the ticket (which is a subcollection of reservation)
            const ticketDocRef = doc(DB, "reservations", reservationId, "tickets", ticketId);
            
            const updateData: Record<string, any> = {
              "userinfos.firstName": firstName,
              "userinfos.lastName": lastName,
              "userinfos.lastUpdated": serverTimestamp(),
              "userinfos.updatedBy": userId
            };
            
            // Only update email if provided
            if (email) {
              updateData["userinfos.email"] = email;
            }
            
            batch.update(ticketDocRef, updateData);
          }
          
          // Add audit trail
          const auditLogsCollection = collection(DB, "audit_logs");
          const auditDocRef = doc(auditLogsCollection);
          
          batch.set(auditDocRef, {
            action: isReservationEdit ? "update_reservation" : "update_ticket",
            entityId: isReservationEdit ? reservationId : ticketId,
            entityType: isReservationEdit ? "reservation" : "ticket",
            changes: {
              firstName,
              lastName,
              email
            },
            timestamp: serverTimestamp(),
            userId
          });
          
          await batch.commit();
          
          return { data: { success: true } };
        } catch (error) {
          console.error("Error updating reservation info:", error);
          return { error: "Failed to update information" };
        }
      },
      invalidatesTags: (result, error, { reservationId }) => [
        { type: 'Reservation', id: reservationId }
      ]
    }),
    updateTicketUserInfo: builder.mutation<{ success: boolean }, UpdateTicketUserInfo>({
      queryFn: async ({ 
        reservationId, 
        ticketId, 
        userId, 
        firstName, 
        lastName, 
        email
      }) => {
        try {
          const batch = writeBatch(DB);
          
          // Use the correct document path for tickets
          const ticketDocRef = doc(DB, "reservations", reservationId, "tickets", ticketId);
          
          // Create userinfo object to update
          const userInfoUpdate: Record<string, any> = {
            "userinfos.firstName": firstName,
            "userinfos.lastName": lastName,
            "userinfos.name": `${firstName} ${lastName}`,
            "userinfos.lastUpdated": serverTimestamp(),
            "userinfos.updatedBy": userId
          };
          
          // Only add optional fields if they're provided
          if (email) {
            userInfoUpdate["userinfos.email"] = email;
          }
          
         
          
          batch.update(ticketDocRef, userInfoUpdate);
          
          // Add audit trail
          const auditLogsCollection = collection(DB, "audit_logs");
          const auditDocRef = doc(auditLogsCollection);
          
          batch.set(auditDocRef, {
            action: "update_ticket_user_info",
            entityId: ticketId,
            entityType: "ticket",
            reservationId: reservationId,
            changes: {
              firstName,
              lastName,
              email
            },
            timestamp: serverTimestamp(),
            userId
          });
          
          await batch.commit();
          
          return { data: { success: true } };
        } catch (error: any) {
          console.error("Error updating ticket user info:", error);
          return { error: error.message || "Failed to update ticket information" };
        }
      },
      invalidatesTags: (result, error, { reservationId, ticketId }) => [
        { type: 'Reservation', id: reservationId },
        { type: 'Tickets', id: ticketId }
      ]
    }),
    refundTicket: builder.mutation<RefundResponse, RefundRequest>({
      async queryFn(refundData) {
        try {
          const { currentUser } = AUTH;
          if (!currentUser) {
            console.error('User not authenticated');  
            return {
              error: {
                status: 'AUTH_ERROR',
                data: 'USER NOT AUTHENTICATED',
              }
                    
            }
          }
    
          const idToken = await currentUser.getIdToken();
          const getStripeRefund = httpsCallable<RefundRequest, RefundResponse>(
            FUNCTIONS, 
            'getStripeRefund'
          );

          const refundParams = {
            amount: refundData.amount,
            ticketid: refundData.ticketid,
            eventid: refundData.eventid,
            reservationid: refundData.reservationid,
            pi: refundData.pi,
            userid: refundData.userid,
            idToken
          }

          const response = await getStripeRefund(refundParams);
          console.log(response.data);

          return { data: response.data };
        } catch (error: any) {
          console.error(error.message);
          console.error(error.code);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An unknown error occurred',
            },
          };
        }
      },
      // Invalidate relevant cache after refund
      invalidatesTags: ['Tickets', 'Reservations'],
    }),
    refundBusTicket: builder.mutation<BusRefundResponse, BusRefundRequest>({
      async queryFn(refundData) {
        try {
          const { currentUser } = AUTH;
          if (!currentUser) {
            console.error('User not authenticated');  
            return {
              error: {
                status: 'AUTH_ERROR',
                data: 'USER NOT AUTHENTICATED',
              }
            };
          }

          const idToken = await currentUser.getIdToken();
          const refundBusTicket = httpsCallable<BusRefundRequest, BusRefundResponse>(
            FUNCTIONS, 
            'refundBusTicket'
          );

          
          
          const refundParams = {
            amount: refundData.amount,
            busTicketId: refundData.busTicketId,
            eventId: refundData.eventId,
            reservationId: refundData.reservationId,
            pi: refundData.pi,
            userId: refundData.userId,
            ticketType: refundData.ticketType,
            idToken
          };
          console.log('Refunding bus ticket with params:', refundParams);

          const response = await refundBusTicket(refundParams);
          console.log('Bus refund response:', response.data);

          return { data: response.data };
        } catch (error: any) {
          console.error('Bus refund error:', error.message);
          console.error('Bus refund error code:', error.code);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An unknown error occurred',
            },
          };
        }
      },
      // Invalidate relevant cache after refund
      invalidatesTags: (result, error, arg) => [
        { type: 'Reservation', id: arg.reservationId },
        { type: 'Reservations', id: 'LIST' },
        { type: 'BusTicket', id: arg.busTicketId },
        { type: 'Tickets', id: 'LIST' },
        { type: 'Event', id: arg.eventId },
      ],
    }),
    sendConfirmationEmail: builder.mutation<void, { mode: string, reservationid: string }>({
      async queryFn({ mode, reservationid }) {
        try {
          const createMailTask = httpsCallable(FUNCTIONS, 'createMailTask');
          await createMailTask({ mode, reservationid });
          return { data: undefined };
        } catch (error: any) {
          return { error: error.message };
        }
      }
    }),
    
  }),
});

export const { useGetAttendeesQuery, 
  useGetConfirmedTicketIdsQuery,
  useSendConfirmationEmailMutation,
  useUpdateReservationUserInfoMutation,
  useUpdateTicketUserInfoMutation,
  useRefundTicketMutation,
  useRefundBusTicketMutation
} = ReservationsApi;

